"use client"

import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Staff } from '@/types';
import { authService } from '@/services/authService';

interface StaffContextType {
  currentStaff: Staff | null;
  login: (email: string, password: string) => Promise<{ success: boolean; redirectTo?: string }>;
  logout: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
}

const StaffContext = createContext<StaffContextType | undefined>(undefined);

export function StaffProvider({ children }: { children: ReactNode }) {
  const [currentStaff, setCurrentStaff] = useState<Staff | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const login = async (email: string, password: string): Promise<{ success: boolean; redirectTo?: string }> => {
    try {
      // Use auth service for proper authentication
      const authResponse = await authService.login({ email, password });
      
      if (authResponse.status === "success" && authResponse.data?.user) {
        const user = authResponse.data.user;
        
        // Check if user is staff (role should be STAFF or ADMIN)
        if (user.role === "STAFF" || user.role === "ADMIN") {
          // Create staff object from user data
          const staff: Staff = {
            id: user.id,
            name: user.name,
            phone: user.phone || "",
            email: user.email || "",
            password: "",
            role: user.role === "ADMIN" ? "manager" : "technician",
            status: "active",
            joinedDate: new Date(user.createdAt || new Date()),
            totalJobs: 0,
          };
          
          setCurrentStaff(staff);
          // Store in localStorage for persistence
          if (typeof window !== 'undefined') {
            localStorage.setItem('staffId', staff.id);
          }
          
          // Role-based redirection
          const redirectTo = staff.role === 'manager' ? '/' : '/staff/dashboard';
          
          return { success: true, redirectTo };
        } else {
          return { success: false };
        }
      }
      return { success: false };
    } catch (error) {
      console.error("Login error:", error);
      return { success: false };
    }
  };

  const logout = () => {
    setCurrentStaff(null);
    if (typeof window !== 'undefined') {
      localStorage.removeItem('staffId');
    }
  };

  // Check for existing session on mount
  React.useEffect(() => {
    const checkSession = () => {
      if (typeof window !== 'undefined') {
        // Check if user is authenticated via auth context
        const userStr = localStorage.getItem('user');
        const staffId = localStorage.getItem('staffId');
        
        if (userStr && staffId) {
          try {
            const user = JSON.parse(userStr);
            // Only restore session if user is staff
            if (user.role === "STAFF" || user.role === "ADMIN") {
              const staff: Staff = {
                id: user.id,
                name: user.name,
                phone: user.phone || "",
                email: user.email || "",
                password: "",
                role: user.role === "ADMIN" ? "manager" : "technician",
                status: "active",
                joinedDate: new Date(user.createdAt || new Date()),
                totalJobs: 0,
              };
              setCurrentStaff(staff);
            } else {
              localStorage.removeItem('staffId');
            }
          } catch (error) {
            localStorage.removeItem('staffId');
          }
        }
        setIsLoading(false);
      }
    };
    checkSession();
  }, []);

  const value: StaffContextType = {
    currentStaff,
    login,
    logout,
    isAuthenticated: currentStaff !== null,
    isLoading,
  };

  return <StaffContext.Provider value={value}>{children}</StaffContext.Provider>;
}

export function useStaff() {
  const context = useContext(StaffContext);
  if (context === undefined) {
    throw new Error('useStaff must be used within a StaffProvider');
  }
  return context;
}